import { Injectable, Injector } from "@angular/core";
import { getCurrentUser, signIn, signInWithRedirect, type SignInInput, type SignInOutput } from "@aws-amplify/auth";
import { fetchAuthSession } from "aws-amplify/auth";
import { environment } from "src/environments/environment";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { Hub } from '@aws-amplify/core';
import { Store, select } from "@ngrx/store";
import { ConfirmResetPasswordInput, ConfirmSignInInput, ConfirmSignInOutput, ResetPasswordInput, ResetPasswordOutput, SignUpInput, SignUpOutput, UpdateUserAttributeOutput, autoSignIn, confirmResetPassword, confirmSignIn, confirmSignUp, fetchUserAttributes, resendSignUpCode, resetPassword, signOut, signUp, updatePassword, updateUserAttributes } from "aws-amplify/auth";
import { NGXLogger } from "ngx-logger";
import { BehaviorSubject, firstValueFrom, catchError, of } from "rxjs";
import { filter, take } from "rxjs/operators";
import { ConfirmSignUpAction, SignUpActionTypes, SignUpCompleteAction, SignUpFailedAction } from "src/app/components/allusers/signup/signup.actions";
import { SignUpState } from "src/app/components/allusers/signup/signup.model";
import { User } from "../../../app/models/user.model";
import {
  ForgotPasswordAction,
  ForgotPasswordFailed,
  ForgotPasswordSubmitted,
  NewPasswordRequiredAction,
  SignInActionTypes,
  SignInFailedAction,
  SignedInAction,
  SignedOutAction
} from "../../components/allusers/signin/signin.actions";
import { SignInState } from "../../components/allusers/signin/signin.model";
import { getSignIn } from "../../components/allusers/signin/signin.selector";
import { RegmembersService } from "../regmembers.service";
import { AmplifyAuthEvent, AmplifyStateChangedAction } from "./amplify.actions";
import { isAmplifySignedIn, isAmplifySignedOut } from "./amplify.selectors";
import { RouterService } from "src/app/services/route-service/router.service";
import { commonTokenProvider } from "src/main";
import { FederatedLogin } from "./base.token.provider";
import { FederatedIdentityService } from "src/app/services/federated-identity.service";


const initialAuthState = {
  currentState: SignInActionTypes.SignedOut
} as SignInState;

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private readonly _authState = new BehaviorSubject<SignInState>(
    initialAuthState
  );

  private isAmplifySignedIn$ = this.amplifyStore.pipe(
    select(isAmplifySignedIn),
    filter(val => (val ? true : false))
  );
  private isAmplifySignedOut$ = this.amplifyStore.pipe(
    select(isAmplifySignedOut),
    filter(val => (val ? true : false))
  );
  currentUser;

  constructor(
    private regmembersService: RegmembersService,
    private signInStore: Store<SignInState>,
    private amplifyStore: Store<AmplifyAuthEvent>,
    private signUpStore: Store<SignUpState>,
    private injector: Injector,
    private logger: NGXLogger,
    private federatedIdentityService: FederatedIdentityService
    ) {
    this.isAmplifySignedIn$.subscribe(authEvent => {
      if (authEvent && authEvent.event == "signIn") {
        logger.info("Amplify User Signed In...");
      }
    });

    this.isAmplifySignedOut$.subscribe(authEvent => {
      if (
        window &&
        !(
          window.location.href.indexOf("token") > 0 &&
          window.location.href.indexOf("ssologin")
        )
      ) {
        if (authEvent && authEvent.event == "signOut") {
          logger.info("Amplify User Signed Out...");
          this.logoutUser();
        }
      }
    });

    this.registerAmplifyEvents();
  }

  private registerAmplifyEvents() {
    // Use Hub channel 'auth' to get notified on changes
    Hub.listen('auth', ({ payload: { event, message } }) => {
      this.logger.info(`Amplify Event - ${event}`);

      this.amplifyStore.dispatch(new AmplifyStateChangedAction({ event, message } as AmplifyAuthEvent));

    });
  }

  public async loggedInUser(cognitoUser: any) {
    if (!this.currentUser) {
      let username;
      if (cognitoUser && cognitoUser.username) {
        username = cognitoUser.username;
      } else {
        throw new Error("Error - Unable to get loggedIn user");
      }

      try {
        const user = await this.regmembersService.getUserBy(username);

        if (user) {
          const memberCourses = await this.regmembersService.getCoursesBy(username);
          this.signInStore.dispatch(
            new SignedInAction({
              currentState: SignInActionTypes.SignedIn,
              user,
              memberCourses
            } as SignInState)
          );
          this.currentUser = user;          
          return this.currentUser;
        }
      } catch (error) {
        this.logger.error(error);
        throw new Error("Error - Unable to get loggedIn user");
      }
    }
    return this.currentUser;
  }

  private logoutUser() {
    this.signOut();

    return this.currentUser;
  }

  public async federatedSignIn(provider: string, googleToken: string) {
    try {
      this.currentUser = null;

      let domain = '';
      if (provider === 'google') {
        domain = "accounts.google.com";
      } else {
        this.logger.error(`Unsupported provider: ${provider}`);
        throw new Error(`Unable to log you in, please contact keystodriving support.`);
      }       
      
      const { member, token, href } = await this.federatedIdentityService.authorize(provider, googleToken);
      commonTokenProvider.loadFederatedLogin({
        domain,
        token: token,
        username: member.membername
      } as FederatedLogin);
      const session = await fetchAuthSession();      

      await new Promise(resolve => setTimeout(resolve, 1000));
      await this.loggedInUser({ username: member.membername });       
      
      this.logger.info(`Federated Sign In Successful for ${provider} user: ${member.membername}`);
    } catch (error) {
      this.logger.error(`Federated Sign In Failed - ${JSON.stringify(error)}`);
      this.signInStore.dispatch(
        new SignInFailedAction({
          currentState: SignInActionTypes.SignInFailed,
          error: error.message,
        } as SignInState)
      );
      throw error;
    }
  }

  public async signIn(username, password) {
    try {
      this.currentUser = null;
      const signInInput = {
        username, password, options: {
          authFlowType: "USER_PASSWORD_AUTH"
        }
      } as SignInInput;
      const signInOutput = await signIn(signInInput);
      this.handleSignInSteps(username, signInOutput);
    } catch (error) {
      this.logger.error(`User Logged In Failed - ${JSON.stringify(error)}`);

      this.logger.error(`Sign Error - ${JSON.stringify(error)}`);
      this.signInStore.dispatch(
        new SignInFailedAction({
          currentState: SignInActionTypes.SignInFailed,
          error: error.message,
        } as SignInState)
      );
      this.signOut();
    }
  }

  private async handleSignInSteps(username: string | null, signInOutput: SignInOutput | ConfirmSignInOutput) {
    this.logger.info(`Handling SignInSteps - ${JSON.stringify(signInOutput)}`);
    const { isSignedIn, nextStep } = signInOutput;

    switch (nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        // CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED - The user was created with a temporary password and must set a new one. Complete the process with confirmSignIn.
        this.logger.info("SignInStep - CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED...");
        this.signInStore.dispatch(
          new NewPasswordRequiredAction({
            currentState: SignInActionTypes.NewPasswordRequired,
          } as SignInState)
        );
        break;
      case 'CONFIRM_SIGN_UP':
        // CONFIRM_SIGN_UP - The user hasn't completed the sign-up flow fully and must be confirmed via confirmSignUp.
        this.logger.info("SignInStep - CONFIRM_SIGN_UP...");
        this.signUpStore.dispatch(
          new ConfirmSignUpAction({
            currentState: SignUpActionTypes.ConfirmSignUp,
            signInOutput
          } as SignUpState)
        );
        break;
      case 'RESET_PASSWORD':
        // RESET_PASSWORD - The user must reset their password via resetPassword.
        this.logger.info("SignInStep - RESET_PASSWORD...");
        this.resetPassword(username);
        break;
      case 'DONE':
        this.logger.info("User Logged In Succesfully...");
        this.logger.info(`currentUser - ${this.currentUser}`);
        await this.getLoggedInUser();
        break;
    }
  }

  public async confirmSignIn(code) {
    try {
      const confirmSignInOutput = await confirmSignIn({ challengeResponse: code } as ConfirmSignInInput);
      this.handleSignInSteps(null, confirmSignInOutput);
    } catch (error) {
      this.logger.error(`ConfirmSignIn Failed - ${JSON.stringify(error)}`);

      this.signInStore.dispatch(
        new SignInFailedAction({
          currentState: SignInActionTypes.SignInFailed,
          error: error.message,
        } as SignInState));
    }
  }

  /**
   * Returns a user-friendly message for forgot-password errors instead of raw Cognito/API messages.
   */
  public getFriendlyForgotPasswordMessage(error: any): string {
    const raw = error?.message || String(error);
    if (!raw) {
      return "We couldn't complete your request. Please check your information and try again.";
    }
    const lower = raw.toLowerCase();
    if (lower.includes("user not found") || lower.includes("username") && lower.includes("exist")) {
      return "We couldn't find an account with that username. Please check it and try again.";
    }
    if (lower.includes("code") && (lower.includes("invalid") || lower.includes("expired"))) {
      return "That verification code is invalid or has expired. Please request a new code.";
    }
    if (lower.includes("limit") || lower.includes("throttl")) {
      return "Too many attempts. Please wait a few minutes and try again.";
    }
    return "We couldn't reset your password. Please check your details and try again, or contact support if the problem continues.";
  }

  public async resetPassword(username) {
    try {
      const resetPasswordOutput = await resetPassword({ username } as ResetPasswordInput);
      this.handleResetPassword(resetPasswordOutput);
    } catch (error) {
      this.logger.error(`ResetPassword Failed - ${JSON.stringify(error)}`);

      this.signInStore.dispatch(
        new ForgotPasswordFailed({
          currentState: SignInActionTypes.ForgotPasswordFailed,
          error: this.getFriendlyForgotPasswordMessage(error),
        } as SignInState));
    }
  }

  public async confirmResetPassword({
    username,
    confirmationCode,
    newPassword
  }: ConfirmResetPasswordInput) {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
    } catch (error) {
      this.logger.error(`Unable to confirmResetPassword - ${JSON.stringify(error)}`);
      this.signInStore.dispatch(
        new ForgotPasswordFailed({
          currentState: SignInActionTypes.ForgotPasswordFailed,
          error: this.getFriendlyForgotPasswordMessage(error),
        } as SignInState)
      );
    }
  }

  private async handleResetPassword(resetPasswordOutput: ResetPasswordOutput) {
    this.logger.info(`Handling ResetPassword - ${JSON.stringify(resetPasswordOutput)}`);
    const { isPasswordReset, nextStep } = resetPasswordOutput;

    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        this.logger.info("ForgotPassword - CONFIRM_RESET_PASSWORD_WITH_CODE...");
        this.signInStore.dispatch(
          new ForgotPasswordAction({
            currentState: SignInActionTypes.ForgotPassword,
          } as SignInState)
        );
        break;
      case 'DONE':
        this.logger.info("ForgotPassword - reset Succesfully...");
        this.signInStore.dispatch(
          new ForgotPasswordSubmitted({
            currentState: SignInActionTypes.ForgotPasswordSubmitted,
          } as SignInState)
        );
    }
  }

  public async newPasswordRequired(cognitoUser, password) {
    await this.confirmSignIn(password);
  }

  public async signOut() {
    try {
      await signOut({ global: true });
      this.logger.info("Amplify User Logged Out Successfully...");

      // Clear federated login from localStorage
      commonTokenProvider.clearFederatedLogin();

      // Clear Cognito Identity session storage
      this.clearCognitoIdentityStorage();

      if (this.currentUser) {
        this.currentUser = null;
        this.signInStore.dispatch(
          new SignedOutAction({
            currentState: SignInActionTypes.SignedOut
          } as SignInState)
        );
      }
    } catch (error) {
      this.logger.error(
        `Amplify User Logged Out Failed - ${JSON.stringify(error)}`
      );
      // Still clear federated login and storage even if signOut fails
      commonTokenProvider.clearFederatedLogin();
      this.clearCognitoIdentityStorage();
    }
  }

  // Clear Cognito Identity session storage (localStorage and sessionStorage)
  private clearCognitoIdentityStorage() {
    try {
      if (typeof window !== 'undefined') {
        // Common Cognito Identity storage keys
        const cognitoKeys = [
          'CognitoIdentityServiceProvider',
          'aws-amplify-cache',
          'aws-amplify-federatedInfo',
          'aws-amplify-auth-cache',
          'CognitoIdentityCredentials',
          'aws.cognito.identity-id',
          'aws.cognito.credentials'
        ];

        // Clear localStorage items related to Cognito Identity
        const keysToRemove: string[] = [];
        
        // Check specific known keys
        cognitoKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            keysToRemove.push(key);
          }
        });
        
        // Find all Cognito-related keys (case-insensitive)
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            const lowerKey = key.toLowerCase();
            if (lowerKey.includes('cognitoidentity') ||
                lowerKey.includes('aws-amplify') ||
                lowerKey.includes('amplify-') ||
                (lowerKey.includes('cognito') && lowerKey.includes('identity'))) {
              if (!keysToRemove.includes(key)) {
                keysToRemove.push(key);
              }
            }
          }
        }
        
        // Remove found keys from localStorage
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
            this.logger.info(`Cleared localStorage key: ${key}`);
          } catch (e) {
            this.logger.error(`Error clearing localStorage key ${key}: ${e}`);
          }
        });

        // Clear sessionStorage items related to Cognito Identity
        const sessionKeysToRemove: string[] = [];
        
        // Check specific known keys
        cognitoKeys.forEach(key => {
          if (sessionStorage.getItem(key)) {
            sessionKeysToRemove.push(key);
          }
        });
        
        // Find all Cognito-related keys in sessionStorage
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key) {
            const lowerKey = key.toLowerCase();
            if (lowerKey.includes('cognitoidentity') ||
                lowerKey.includes('aws-amplify') ||
                lowerKey.includes('amplify-') ||
                (lowerKey.includes('cognito') && lowerKey.includes('identity'))) {
              if (!sessionKeysToRemove.includes(key)) {
                sessionKeysToRemove.push(key);
              }
            }
          }
        }
        
        // Remove found keys from sessionStorage
        sessionKeysToRemove.forEach(key => {
          try {
            sessionStorage.removeItem(key);
            this.logger.info(`Cleared sessionStorage key: ${key}`);
          } catch (e) {
            this.logger.error(`Error clearing sessionStorage key ${key}: ${e}`);
          }
        });

        if (keysToRemove.length === 0 && sessionKeysToRemove.length === 0) {
          this.logger.info('No Cognito Identity storage keys found to clear');
        } else {
          this.logger.info(`Cleared ${keysToRemove.length} localStorage and ${sessionKeysToRemove.length} sessionStorage Cognito Identity keys`);
        }
      }
    } catch (error) {
      this.logger.error(`Error clearing Cognito Identity storage: ${JSON.stringify(error)}`);
    }
  }

  public async signUp(signUpInput: SignUpInput) {

    try {
      const signUpOutput = await signUp(signUpInput);
      this.handleSignUpSteps(signUpInput, signUpOutput);
    } catch (error) {
      this.logger.error(
        `Amplify Failed to SignUp - ${JSON.stringify(error)}`
      );
      throw error;
    }
  }

  async confirmSignUp(userName: string, verificationCode: string) {
    // return Auth.confirmSignUp(userName, verificationCode);
    try {
      this.logger.info(`ConfirmSignUp Starting... - ${JSON.stringify(arguments)}`);
      const confirmSignUpOutput = await confirmSignUp({ username: userName, confirmationCode: verificationCode });
      this.handleSignUpSteps(null, confirmSignUpOutput);
    } catch (error) {
      this.logger.error(`ConfirmSignUp Failed - ${JSON.stringify(error)}`);

      this.signUpStore.dispatch(
        new SignUpFailedAction({
          currentState: SignUpActionTypes.SignUpFailed,
          error: error.message,
        } as SignUpState));
    }
  }

  private async handleSignUpSteps(signUpInput: SignUpInput | null, signUpOutput: SignUpOutput) {
    this.logger.info(`Handling SignUpSteps - ${JSON.stringify(signUpOutput)}`);
    const { isSignUpComplete, nextStep } = signUpOutput;

    switch (nextStep.signUpStep) {
      case 'COMPLETE_AUTO_SIGN_IN':
        this.logger.info("SignUpStep - COMPLETE_AUTO_SIGN_IN...");
        this.autoSignIn();
        break;
      case 'CONFIRM_SIGN_UP':
        // CONFIRM_SIGN_UP - The user hasn't completed the sign-up flow fully and must be confirmed via confirmSignUp.
        this.logger.info("SignUpStep - CONFIRM_SIGN_UP...");
        this.signUpStore.dispatch(
          new ConfirmSignUpAction({
            currentState: SignUpActionTypes.ConfirmSignUp,
            signUpOutput,
            signUpInput,
          } as SignUpState)
        );
        break;
      case 'DONE':
        this.logger.info("SignUpStep - DONE - User SignedUp");
        this.signUpStore.dispatch(
          new SignUpCompleteAction({
            currentState: SignUpActionTypes.SignUpComplete
          } as SignUpState)
        )
        break;
    }
  }

  async autoSignIn() {
    try {
      const signInOutput = await autoSignIn();
      this.handleSignInSteps(null, signInOutput);
    } catch (error) {
      this.logger.error(`AutoSignIn Failed - ${JSON.stringify(error)}`);

      this.signInStore.dispatch(
        new SignInFailedAction({
          currentState: SignInActionTypes.SignInFailed,
          error: error.message,
        } as SignInState));
    }

  }

  async resendVerificationCode(username: string) {
    try {
      const resendSignUpOutput = await resendSignUpCode({ username });
      return resendSignUpOutput;
    } catch (error) {
      this.logger.error(`Failed to send signup code - ${JSON.stringify(error)}`);
      throw error;
    }
  }

  async getLoggedInUser(): Promise<User> {
    try {
      // First check if we have currentUser set (for federated Identity Pool users)
      if (this.currentUser) {
        return this.currentUser;
      }

      // Try to get Cognito User Pool user (only works for User Pool users, not Identity Pool)
      try {
        const user = await getCurrentUser();
        if (user && user.username) {
          return this.loggedInUser(user);
        }        
        throw new Error("User not found via User Pool, trying Identity Pool...");
      } catch (userPoolError) {
        // getCurrentUser() fails for Identity Pool federated users
        // This is expected, so we'll check the auth session and token provider instead
        this.logger.info(`getCurrentUser() failed (may be Identity Pool user) - ${JSON.stringify(userPoolError)}`);
        
        // Check if we have a federated login in the token provider (for Identity Pool users)
        // Use hasFederatedLogin() which will reload from localStorage if needed
        if (commonTokenProvider.hasFederatedLogin()) {
          this.logger.info(`Federated login found in token provider (Identity Pool user)`);
          // For Identity Pool users, get the username from federated login and fetch user from backend
          const federatedLogin = commonTokenProvider.federatedLogin;
          if (federatedLogin && federatedLogin.username) {
            try {
              // Fetch user from backend using the stored username
              return await this.loggedInUser({ username: federatedLogin.username });
            } catch (error) {
              this.logger.error(`Failed to fetch user for Identity Pool user: ${error}`);
              return this.currentUser;
            }
          }
          // No username stored, return currentUser if available
          return this.currentUser;
        } else {
          this.logger.info(`No federated login found in token provider after reload`);
        }
        
        // Check if we have a valid auth session (works for both User Pool and Identity Pool)
        try {
          const session = await fetchAuthSession();
          if (session && (session.credentials || session.tokens)) {
            // We have a valid session, but no User Pool user
            // This means it's likely a federated Identity Pool user
            this.logger.info(`Valid auth session found, but no User Pool user (likely Identity Pool federated user)`);
            // Return currentUser if available, otherwise the guard/component should fetch from backend
            return this.currentUser;
          } else {
            this.logger.info(`fetchAuthSession() returned null or empty session`);
          }
        } catch (sessionError) {
          this.logger.info(`No valid auth session found - ${JSON.stringify(sessionError)}`);
        }
      }

      return this.currentUser;
    } catch (e) {
      this.logger.info(`Unable to get loggedIn user - ${JSON.stringify(e)}`);
      // Don't call logoutUser() here - it would sign out Identity Pool users on page refresh
      return this.currentUser;
    }
  }

  async forgotPassword(username: string) {
    await this.resetPassword(username);
  }

  async forgotPasswordVerifyCode(
    username: string,
    code: string,
    newPassword: string
  ) {
    this.confirmResetPassword({ username, confirmationCode: code, newPassword });
  }

  public async updatePassword(oldPassword, newPassword) {
    try {
      this.logger.info(`Updating user password...`);
      await updatePassword({ oldPassword, newPassword });
      this.logger.info(`Updated user password successfully...`);
    } catch (error) {
      this.logger.error(`Failed to update password - ${JSON.stringify(error)}`);
      throw error;
    }
  }

  public async fetchUserAttributes() {
    try {
      this.logger.info(`FetchingUserAttributes...`);
      const userAttributes = await fetchUserAttributes();
      return userAttributes;
    } catch (error) {
      this.logger.error(`Failed to FetchingUserAttributes - ${JSON.stringify(error)}`);
      throw error;
    }
  }

  public async updateUserAttributes(attributes) {
    try {
      const output = await updateUserAttributes({
        userAttributes: {
          attributes
        }
      });

      for (const attribute of Object.keys(output)) {
        const updateUserAttributeOutput = output[attribute];
        await this.handleUpdateUserAttributeNextSteps(updateUserAttributeOutput);
      }
    } catch (error) {
      this.logger.error(`Failed to UpdateUserAttribute - ${JSON.stringify(error)}`);
      throw error;
    }
  }

  private async handleUpdateUserAttributeNextSteps(output: UpdateUserAttributeOutput) {
    const { nextStep } = output;

    switch (nextStep.updateAttributeStep) {
      case 'CONFIRM_ATTRIBUTE_WITH_CODE':
        // const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        // console.log(
        //   `Confirmation code was sent to ${codeDeliveryDetails?.deliveryMedium}.`
        // );
        // Collect the confirmation code from the user and pass to confirmUserAttribute.
        this.logger.info(`UpdateUserAttribute - CONFIRM_ATTRIBUTE_WITH_CODE Not supported`);
        throw new Error("Unable of update user info");
        break;
      case 'DONE':
        this.logger.info(`UpdateUserAttribute - attribute was successfully updated.`);
        break;
    }
  }
}
